import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Sparkles, ShieldCheck, Clock } from "lucide-react";
import { COMPANY_INFO, SERVICES } from "../data";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-slate-300 pt-16 pb-8 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 pb-12 border-b border-slate-800">
          {/* Brand Column */}
          <div className="flex flex-col gap-4">
            <Link to="/" className="flex items-center gap-2 group">
              <img
                src="https://assets.auxodev.dk/assets/66f352d5-c4b9-4e8b-8d46-eb7bb9ac1d49/500ed420-e032-4255-bacf-bac59ef7cf9a.png"
                alt="Skræp Vinduespuds"
                className="h-10 sm:h-12 w-auto object-contain brightness-0 invert"
              />
            </Link>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">
              Professionel vinduespudsning og ejendomsservice til private og erhverv på hele Fyn. Jeg leverer altid et stribefrit resultat til den aftalte tid.
            </p>
            <div className="flex items-center gap-2 text-xs text-sky-400 font-semibold bg-sky-950/50 border border-sky-900/50 px-3 py-1.5 rounded-lg w-fit mt-2">
              <ShieldCheck className="h-4 w-4" />
              <span>Fuldt forsikret & lokalt forankret</span>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h3 className="text-white font-semibold text-base mb-6 tracking-wide uppercase text-xs">
              Mine Ydelser
            </h3>
            <ul className="space-y-3.5 text-sm">
              {SERVICES.map((service) => (
                <li key={service.id}>
                  <Link
                    to={`/ydelser/${service.slug}`}
                    className="hover:text-sky-400 transition-colors block"
                  >
                    {service.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/ydelser"
                  className="text-sky-400 hover:text-sky-300 font-medium transition-colors block pt-1"
                >
                  Se alle ydelser &rarr;
                </Link>
              </li>
            </ul>
          </div>

          {/* Quick Links Column */}
          <div>
            <h3 className="text-white font-semibold text-base mb-6 tracking-wide uppercase text-xs">
              Hurtige Links
            </h3>
            <ul className="space-y-3.5 text-sm">
              <li>
                <Link to="/" className="hover:text-sky-400 transition-colors block">
                  Forside
                </Link>
              </li>
              <li>
                <Link to="/galleri" className="hover:text-sky-400 transition-colors block">
                  Galleri
                </Link>
              </li>
              <li>
                <Link to="/om-os" className="hover:text-sky-400 transition-colors block">
                  Om Skræp Vinduespuds
                </Link>
              </li>
              <li>
                <Link to="/kontakt" className="hover:text-sky-400 transition-colors block">
                  Kontakt os
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h3 className="text-white font-semibold text-base mb-6 tracking-wide uppercase text-xs">
              Kontakt & Info
            </h3>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-sky-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-medium">Dækningsområde:</p>
                  <p className="text-sky-400 font-semibold">{COMPANY_INFO.geoArea}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{COMPANY_INFO.address}</p>
                </div>
              </li>
              {COMPANY_INFO.phone && (
                <li className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-sky-500 shrink-0" />
                  <a
                    href={`tel:${COMPANY_INFO.phone.replace(/\s+/g, "")}`}
                    className="hover:text-sky-400 transition-colors font-medium whitespace-nowrap"
                  >
                    {COMPANY_INFO.phone}
                  </a>
                </li>
              )}
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-sky-500 shrink-0" />
                <a
                  href={`mailto:${COMPANY_INFO.email}`}
                  className="hover:text-sky-400 transition-colors font-medium break-all"
                >
                  {COMPANY_INFO.email}
                </a>
              </li>
              <li className="flex items-center gap-3 text-slate-400">
                <Clock className="h-5 w-5 text-sky-500 shrink-0" />
                <span>Mandag - Fredag: 07:00 - 17:00</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <p>
            &copy; {currentYear} {COMPANY_INFO.name}. Alle rettigheder forbeholdes.
          </p>
          <p className="flex items-center gap-1">
            <span>Udført med fokus på kvalitet på</span>
            <span className="text-sky-400 font-semibold">{COMPANY_INFO.geoArea}</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
